import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { LEAD_TYPES, track } from "../../lib/analytics";
import { getAttribution } from "../../lib/attribution";

// Megjelenés 10 másodperc böngészés után
const SHOW_AFTER_MS = 10000;

// Ha a süti-sáv még kint van, ennyinként nézzük újra
const BANNER_RETRY_MS = 2000;

// --- gyakoriság-korlátozás ------------------------------------------------
// bezárta                -> 24 óráig ne jöjjön elő
// sikeresen elküldte     -> 30 napig ne jöjjön elő
// egy munkameneten belül -> legfeljebb egyszer
const CLOSED_KEY = "teleport_callback_closed_at";
const SUBMITTED_KEY = "teleport_callback_submitted_at";
const SESSION_KEY = "teleport_callback_seen";

const CLOSED_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const SUBMITTED_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

// A lead típusa mindenhol ugyanaz: callback_popup
const LEAD_TYPE = LEAD_TYPES.visszahivas;

// Ezeken az oldalakon nincs értelme (már konvertált, vagy jogi szöveget olvas)
const SKIP_PATHS = ["/koszonjuk", "/aszf", "/adatvedelem"];

function readTimestamp(key) {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (e) {
    return null;
  }
}

function writeTimestamp(key) {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch (e) {
    // privát mód – ilyenkor legfeljebb a következő látogatáskor újra felajánljuk
  }
}

function seenInThisSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch (e) {
    return false;
  }
}

function markSeenInThisSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch (e) {
    // nincs teendő
  }
}

function mayShow() {
  if (seenInThisSession()) return false;

  const submittedAt = readTimestamp(SUBMITTED_KEY);
  if (submittedAt && Date.now() - submittedAt < SUBMITTED_COOLDOWN_MS) {
    return false;
  }

  const closedAt = readTimestamp(CLOSED_KEY);
  if (closedAt && Date.now() - closedAt < CLOSED_COOLDOWN_MS) {
    return false;
  }

  return true;
}

export default function CallbackPopup() {
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const phoneInput = useRef(null);
  const submitted = useRef(false);

  // --- megjelenítés időzítése -------------------------------------------
  useEffect(() => {
    if (!mayShow()) return;
    if (SKIP_PATHS.some((p) => pathname.startsWith(p))) return;

    let timer;

    const show = () => {
      // Amíg a süti-sáv kint van, nem teszünk rá egy második ablakot – de nem
      // is mondunk le róla: újrapróbáljuk, amint a látogató döntött.
      if (document.querySelector(".alert.fixed-bottom")) {
        timer = setTimeout(show, BANNER_RETRY_MS);
        return;
      }
      if (!mayShow()) return;

      markSeenInThisSession();
      setOpen(true);
      track("callback_popup_view", {
        lead_type: LEAD_TYPE,
        page_path: window.location.pathname,
      });
    };

    timer = setTimeout(show, SHOW_AFTER_MS);

    return () => clearTimeout(timer);
  }, [pathname]);

  const close = useCallback((reason) => {
    setOpen(false);
    // Sikeres beküldés után nem "bezárás" – azt már a callback_request fedi
    if (submitted.current) return;

    writeTimestamp(CLOSED_KEY);
    track("callback_popup_close", {
      lead_type: LEAD_TYPE,
      dismiss_reason: reason,
      page_path: window.location.pathname,
    });
  }, []);

  // Esc-re zárjon, és nyitáskor a telefonszám mezőbe kerüljön a fókusz
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") close("escape");
    };
    document.addEventListener("keydown", onKeyDown);

    if (phoneInput.current) phoneInput.current.focus();

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);

    const trimmedPhone = phone.trim();

    // A visszahíváshoz csak telefonszám kell. Megengedő ellenőrzés:
    // legalább 7 számjegy.
    if ((trimmedPhone.match(/\d/g) || []).length < 7) {
      track("form_error", { form_name: "callback_popup", field_name: "phone" });
      setError("Kérjük, adjon meg egy érvényes telefonszámot.");
      return;
    }
    if (!accepted) {
      track("form_error", {
        form_name: "callback_popup",
        field_name: "terms_and_conditions",
      });
      setError("Az Adatvédelmi Szabályzat elfogadása kötelező.");
      return;
    }

    setSending(true);

    const url = process.env.REACT_APP_API_URL;
    const payload = {
      phone: trimmedPhone,
      lead_type: LEAD_TYPE,
      page_url: window.location.href,
      created_at: new Date().toISOString(),
      // utm_*, fbclid, gclid, referrer, landing_page – az érkezéskor elkapva
      ...getAttribution(),
    };

    let response;
    try {
      response = await axios.post(`${url}/api/callback-request`, payload);
    } catch (e) {
      setSending(false);
      setError("A küldés nem sikerült. Kérjük, próbálja újra, vagy hívjon minket.");
      track("form_submit_error", {
        form_name: "callback_popup",
        error_message: e?.message || "halozati hiba",
      });
      return;
    }

    // Az API "status"-t ad vissza, de "success"-re is felkészülünk
    if (response?.data?.success === false || response?.data?.status === false) {
      setSending(false);
      setError("A küldés nem sikerült. Kérjük, próbálja újra, vagy hívjon minket.");
      track("form_submit_error", {
        form_name: "callback_popup",
        error_message:
          response?.data?.message || "a backend sikertelenseget jelzett",
      });
      return;
    }

    // Innentől biztosan sikeres – CSAK itt keletkezik lead
    setSending(false);
    setDone(true);
    submitted.current = true;
    writeTimestamp(SUBMITTED_KEY);

    track("callback_request", { lead_type: LEAD_TYPE });
    track("generate_lead", { lead_type: LEAD_TYPE });

    setTimeout(() => setOpen(false), 4000);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="callback-popup-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) close("backdrop");
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(20, 12, 34, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "10px",
          maxWidth: "440px",
          width: "100%",
          padding: "28px 24px 24px",
          position: "relative",
          boxShadow: "0 18px 48px rgba(0,0,0,.3)",
        }}
      >
        <button
          type="button"
          onClick={() => close("close_button")}
          aria-label="Bezárás"
          style={{
            position: "absolute",
            top: "10px",
            right: "12px",
            background: "none",
            border: "none",
            fontSize: "26px",
            lineHeight: 1,
            color: "#8c8aa3",
            cursor: "pointer",
          }}
        >
          &times;
        </button>

        {done ? (
          <div className="text-center">
            <div style={{ fontSize: "42px", lineHeight: 1 }}>📞</div>
            <h4
              id="callback-popup-title"
              style={{ color: "#472c6b", marginTop: "12px" }}
            >
              Köszönjük!
            </h4>
            <p style={{ color: "#333333", margin: 0 }}>
              Hamarosan visszahívjuk a megadott számon.
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h4
              id="callback-popup-title"
              style={{ color: "#472c6b", marginBottom: "6px" }}
            >
              Visszahívjuk Önt!
            </h4>
            <p style={{ color: "#555", fontSize: "14px" }}>
              Hagyja itt a telefonszámát, és munkatársunk rövid időn belül
              jelentkezik egy díjmentes árajánlattal.
            </p>

            <div className="form-group mb-2">
              <input
                ref={phoneInput}
                type="tel"
                className="form-control"
                placeholder="Telefonszám"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {/* A kapcsoló és a szöveg külön <label>, a link viszont MINDKETTŐN
                KÍVÜL van – így a linkre kattintás csak megnyitja a szabályzatot,
                a pipát nem billenti, a szövegre kattintás pedig nem navigál. */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                padding: "10px 0",
              }}
            >
              <label
                htmlFor="callback-terms"
                style={{
                  margin: 0,
                  flex: "0 0 auto",
                  display: "inline-flex",
                  cursor: "pointer",
                }}
              >
                <input
                  id="callback-terms"
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  style={{
                    position: "absolute",
                    opacity: 0,
                    width: 0,
                    height: 0,
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "7px",
                    border: accepted ? "2px solid #fbb43e" : "2px solid #c9c6d6",
                    background: accepted ? "#fbb43e" : "#ffffff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "background .15s ease, border-color .15s ease",
                  }}
                >
                  {accepted && (
                    <svg width="15" height="15" viewBox="0 0 16 16">
                      <path
                        d="M2.5 8.5l3.5 3.5 7.5-8"
                        fill="none"
                        stroke="#ffffff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
              </label>

              <span
                style={{
                  fontSize: "13.5px",
                  color: "#555",
                  lineHeight: 1.45,
                  paddingTop: "3px",
                  userSelect: "none",
                }}
              >
                <label
                  htmlFor="callback-terms"
                  style={{
                    margin: 0,
                    display: "inline",
                    fontWeight: 400,
                    textTransform: "none",
                    cursor: "pointer",
                  }}
                >
                  Megismertem és elfogadom az
                </label>{" "}
                <a
                  href="/adatvedelem"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "#472c6b" }}
                >
                  Adatvédelmi Szabályzatot
                </a>
                .
              </span>
            </div>

            {error && (
              <p style={{ color: "#c0392b", fontSize: "13px", marginTop: "4px" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={sending}
              style={{
                width: "100%",
                padding: "12px 18px",
                border: "none",
                borderRadius: "5px",
                background: "#fbb43e",
                color: "#ffffff",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".5px",
                cursor: sending ? "default" : "pointer",
                opacity: sending ? 0.7 : 1,
              }}
            >
              {sending ? "Küldés…" : "Kérem a visszahívást"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
